// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import DataTable from "@insite/mobius/DataTable";
import { DataTableBody, DataTableCell, DataTableHead, DataTableHeader, DataTableRow, Link } from "@insite/mobius/index";
import translate from "@insite/client-framework/Translate";
import { tableProps } from "@store/Themes/common/props/table";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import MobiusAccordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import ManagedAccordionSection from "@store/MobiusOverrides/AccordionSection/ManagedAccordionSection";
import { AccordionSectionPanel, AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { accordionProductDetailsSectionProps } from "@store/Themes/common/props/accordionProductDetails";

const enum fields {
    numberOfItems = "numberOfItems",
}

export interface ProductDetailsAccessoriesProps extends WidgetProps {
    fields: {
        [fields.numberOfItems]: number;
    };
}

type Props = ProductDetailsAccessoriesProps & HasProduct;

export interface AccessoriesUIModel {
    code: string;
    description: string;
    type: string;
}

export interface ProductDetailsAccessoriesStyles {
    seeMoreLinkWrapper?: InjectableCss;
    tableWrapper?: InjectableCss;
    accessoriesHeader?: InjectableCss;
    accessoriesBuild?: InjectableCss;
    accessoriesBuildLabel?: TypographyPresentationProps;
    accessoriesUniqueIdentifier?: TypographyPresentationProps;
    accessoriesDescription?: TypographyPresentationProps;
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
}

export const productDetailsAccessoriesStyles: ProductDetailsAccessoriesStyles = {
    seeMoreLinkWrapper: {
        css: css`
            margin-top: ${pxToRem(15)};
        `,
    },
    tableWrapper: tableProps.wrapper,
    accessoriesHeader: {
        css: css`
            display: flex;
            align-items: center;
            margin-bottom: ${pxToRem(15)};
        `,
    },
    accessoriesBuild: {
        css: css`
            background-color: ${getColor("secondary")};
            color: ${getColor("secondary.contrast")};
            border-radius: ${pxToRem(10)};
            padding: ${pxToRem(5, 15)};
            display: flex;
            flex-direction: column;
            margin-right: ${pxToRem(15)};
        `,
    },
    accessoriesBuildLabel: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smallest};
            text-transform: uppercase;
            text-align: center;
        `,
    },
    accessoriesUniqueIdentifier: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.larger};
            font-weight: ${x => x.theme.design.typography.weight.semibold};
            text-align: center;
        `,
    },
    accordion: {
        css: css`
            margin-top: ${pxToRem(30)};
        `,
    },
    accordionSection: {
        headerProps: {
            ...accordionProductDetailsSectionProps.headerProps,
            css: css`
                ${accordionProductDetailsSectionProps.headerProps?.css}

                ${AccordionSectionPanel} + && {
                    margin-top: ${pxToRem(15)};
                }

                && button[aria-expanded="true"] {
                    border-color: ${getColor("common.border")};
                }
            `,
        },
        panelProps: {
            ...accordionProductDetailsSectionProps.panelProps,
            css: css`
                ${accordionProductDetailsSectionProps.panelProps?.css}
            `,
        },
        mergeCss: true,
    },
};

const styles = productDetailsAccessoriesStyles;

const ProductDetailsAccessories = ({ product, fields: { numberOfItems } }: Props) => {
    const [expanded, setExpanded] = React.useState(false);

    const productAccessories: AccessoriesUIModel[] = product.properties.productAccessories
        ? JSON.parse(product.properties.productAccessories)
        : null;

    if (!productAccessories || productAccessories.length === 0) {
        return null;
    }

    const limitedProductAccessories = expanded ? productAccessories : productAccessories.slice(0, numberOfItems);

    return (
        <MobiusAccordion headingLevel={1} {...styles.accordion}>
            <ManagedAccordionSection
                initialExpanded={true}
                title={translate("Included Accessories")}
                {...styles.accordionSection}
            >
                <StyledWrapper>
                    <StyledWrapper {...styles.accessoriesHeader}>
                        <StyledWrapper {...styles.accessoriesBuild}>
                            <Typography {...styles.accessoriesBuildLabel}>{translate("Build")}</Typography>
                            <Typography {...styles.accessoriesUniqueIdentifier}>
                                {product.properties.uniqueIdentifier}
                            </Typography>
                        </StyledWrapper>
                        <Typography {...styles.accessoriesDescription}>
                            {translate(
                                "The {0} build if part {1} ships with the controls and accessories below included. These are supplied with the complete valve - no separate ordering required.",
                                product.properties.uniqueIdentifier,
                                product.productNumber,
                            )}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.tableWrapper}>
                        <DataTable>
                            <DataTableHead>
                                <DataTableHeader>{translate("Model code")}</DataTableHeader>
                                <DataTableHeader>{translate("Description")}</DataTableHeader>
                                <DataTableHeader>{translate("Type")}</DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {limitedProductAccessories.map(accessory => (
                                    <DataTableRow key={accessory.code}>
                                        <DataTableCell>{accessory.code}</DataTableCell>
                                        <DataTableCell>{accessory.description}</DataTableCell>
                                        <DataTableCell>{accessory.type}</DataTableCell>
                                    </DataTableRow>
                                ))}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                    {productAccessories.length > numberOfItems && (
                        <StyledWrapper {...styles.seeMoreLinkWrapper}>
                            {expanded ? (
                                <Link onClick={() => setExpanded(false)}>{translate("See Less")}</Link>
                            ) : (
                                <Link onClick={() => setExpanded(true)}>{translate("See All")}</Link>
                            )}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </ManagedAccordionSection>
        </MobiusAccordion>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAccessories),
    definition: {
        displayName: "Product Accessories",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.numberOfItems,
                displayName: "Number of items displayed",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 10,
            },
        ],
    },
};

export default widgetModule;
